import { Component, computed, inject, input } from '@angular/core';
import { Booking } from '../../../../core/models/entities/booking';
import {
  Invoice,
  InvoiceLine,
  InvoicingStatus,
  InvoicingStatusHumanized,
  PayrexxInvoiceStatus,
} from '../../../../core/models/entities/invoice';
import { ChfPipe } from '../../../../core/pipes/chf.pipe';

@Component({
  selector: 'app-customer-invoices-table',
  providers: [ChfPipe],
  template: `
    <div>
      <div class="mb-4 flex items-start justify-between">
        <h3>Facturation</h3>
        @if (booking().invoicing_status && booking().balance !== 0) {
          <app-badge
            [variant]="
              InvoicingStatusHumanized[booking().invoicing_status].color
            "
            class="ml-4"
          >
            {{ balanceLabel() }} : {{ booking().balance | chf }}
          </app-badge>
        }
        @if (booking().balance === 0) {
          <app-badge variant="success" class="ml-4">{{
            InvoicingStatusHumanized[booking().invoicing_status].label
          }}</app-badge>
        }
      </div>

      <h4 class="mb-4 text-base font-medium">Factures</h4>

      <app-table
        [data]="invoices()"
        [trackByFn]="trackByInvoiceId"
        [clickableRows]="false"
        emptyStateTitle="Aucune facture"
        emptyStateMessage="Aucune facture n'est disponible pour cette réservation."
        emptyStateIcon="far fa-file-invoice"
      >
        <!-- Document Column -->
        <app-table-column
          field="document"
          header="Document"
          [showLabelOnMobile]="false"
          width="300px"
        >
          <ng-template let-invoice>
            <div>
              <div class="flex gap-2">
                @if (invoice.metadata.payrexx_id) {
                  <span>
                    Facture {{ invoice.booking_id }}
                    @if (invoice.series) {
                      /{{ invoice.series }}
                    }
                  </span>
                  <i
                    class="far fa-question-circle"
                    tooltip="Payable directement en ligne ou par QR code"
                  ></i>
                } @else {
                  <span>
                    Document {{ invoice.booking_id }}
                    @if (invoice.series) {
                      /{{ invoice.series }}
                    }
                  </span>
                  <i
                    class="far fa-question-circle"
                    tooltip="Document de facturation pour vos dossiers"
                  ></i>
                }
              </div>

              @if (invoice.grouped_invoice_lines) {
                <div class="mt-1.5 text-gray-500">
                  @for (
                    category of getInvoiceCategories(invoice);
                    track category
                  ) {
                    @for (
                      line of invoice.grouped_invoice_lines[category];
                      track line.id
                    ) {
                      <div
                        [innerHTML]="formatInvoiceLine(invoice, line)"
                      ></div>
                    }
                  }
                </div>
              }
            </div>
          </ng-template>
        </app-table-column>

        <!-- Date Column -->
        <app-table-column
          field="date"
          header="Date"
          [showLabelOnMobile]="false"
        >
          <ng-template let-invoice>
            {{ invoice.created_at | date: 'dd.MM.yyyy' }}
          </ng-template>
        </app-table-column>

        <!-- Amount Column -->
        <app-table-column
          field="amount"
          header="Montant"
          headerClasses="justify-end"
          [showLabelOnMobile]="false"
          [alwaysVisibleOnMobile]="true"
        >
          <ng-template let-invoice>
            <div class="sm:text-right">
              {{ invoice.net_total | chf }}
            </div>
          </ng-template>
        </app-table-column>

        <!-- Actions Column -->
        <app-table-actions>
          <ng-template let-invoice>
            @if (
              invoice.metadata?.payrexx_status === PayrexxInvoiceStatus.Deleted
            ) {
              <div class="text-red-500">Supprimée</div>
            } @else if (
              invoice.metadata?.payrexx_status === PayrexxInvoiceStatus.Draft
            ) {
              <div class="text-gray-500 italic">En attente</div>
            } @else {
              <div class="flex flex-col items-end gap-2">
                @if (invoice.download_url) {
                  <a
                    [href]="invoice.download_url"
                    target="_blank"
                    class="inline-flex items-center justify-center gap-x-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-800 shadow-xs no-underline transition-all hover:bg-gray-50 hover:border-gray-300 focus:outline-none"
                  >
                    <span class="hidden sm:inline">Télécharger</span>
                    <span class="sm:hidden">PDF</span>
                  </a>
                }
                @if (invoice.metadata.payrexx_payment_link) {
                  <a
                    [href]="invoice.metadata.payrexx_payment_link"
                    target="_blank"
                    class="inline-flex items-center justify-center gap-x-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-800 shadow-xs no-underline transition-all hover:bg-gray-50 hover:border-gray-300 focus:outline-none"
                  >
                    <span class="hidden sm:inline">Payer en ligne</span>
                    <span class="sm:hidden">Payer</span>
                  </a>
                }
              </div>
            }
          </ng-template>
        </app-table-actions>
      </app-table>

      <!-- Paiements Section -->
      @if (booking().payments && booking().payments.length > 0) {
        <div class="mt-8">
          <h4 class="mb-4 text-base font-medium">Paiements</h4>
          <app-customer-payments-table
            [booking]="booking()"
            [payments]="booking().payments"
          >
          </app-customer-payments-table>
        </div>
      }
    </div>
  `,
  standalone: false,
})
export class CustomerInvoicesTableComponent {
  booking = input.required<Booking>();
  invoices = input<Invoice[]>([]);

  private chfPipe = inject(ChfPipe);

  InvoicingStatus = InvoicingStatus;
  PayrexxInvoiceStatus = PayrexxInvoiceStatus;
  InvoicingStatusHumanized = InvoicingStatusHumanized;

  balanceLabel = computed(() => {
    return this.booking().balance < 0 ? 'Solde à rembourser' : 'Solde dû';
  });

  trackByInvoiceId = (invoice: Invoice): number => invoice.id;

  getInvoiceCategories(invoice: Invoice): string[] {
    return Object.keys(invoice.grouped_invoice_lines || {});
  }

  getTotalInvoiceLines(invoice: Invoice): number {
    return Object.values(invoice.grouped_invoice_lines || {}).flat().length;
  }

  formatInvoiceLine(invoice: Invoice, line: InvoiceLine): string {
    const totalLines = this.getTotalInvoiceLines(invoice);
    let formatted = line.description;

    if (line.quantity > 1) {
      formatted += ` (${line.quantity} x ${this.chfPipe.transform(line.unit_price)})`;
    }

    if (totalLines > 1) {
      formatted += ` : ${this.chfPipe.transform(line.total_price)}`;
    }

    return formatted;
  }
}
